import {useLayoutEffect} from 'react';
import {useFieldArray, useWatch} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {EMPTY_PEP_PARTY} from './pep-form.utils';

import type {PepPartyFormValues} from './pep-form.utils';
import type {Control, FieldValues} from 'react-hook-form';

export interface UsePepPartiesParams<T extends FieldValues> {
  control: Control<T>;
  isCorporate: boolean;
  pepPath?: string;
  requirePartiesWhenPep?: boolean;
}

/**
 * Single `useFieldArray` instance for `{pepPath}.parties`, shared by `PepToggleField` and
 * `PepSection` via props. React Hook Form's `useFieldArray` does not broadcast `append`/
 * `replace`/`remove` calls across separate hook instances bound to the same field name —
 * each instance only refreshes from its own subscription — so the two components cannot
 * each call `useFieldArray` independently and expect to see each other's mutations.
 *
 * Also owns the "individual mode always has exactly one party" invariant: `PepSection`
 * renders `parties.0` directly (no relationship-based multi-select) whenever `!isCorporate`,
 * so a party must exist the moment `isPep` is true — not just when the user clicks the
 * toggle, but also when `isCorporate` flips from true to false (e.g. developer/party type
 * changes elsewhere in the form) while PEP was already "Yes". A layout effect (not a
 * regular effect) keeps this in sync before paint, so the interactive toggle path never
 * flashes an empty section for a frame.
 *
 * With `requirePartiesWhenPep`, a corporation that declares PEP must list at least one party:
 * the field-array rule fails the submit with the error on `{pepPath}.parties.root`.
 */
export function usePepParties<T extends FieldValues>({
  control,
  isCorporate,
  pepPath = 'pep',
  requirePartiesWhenPep = false
}: UsePepPartiesParams<T>) {
  const {t} = useTranslation();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const c = control as unknown as Control<any>;

  const isPep = useWatch({control: c, name: `${pepPath}.isPep` as never});

  const {
    fields: rawFields,
    append,
    remove,
    replace
  } = useFieldArray({
    control: c,
    name: `${pepPath}.parties` as never,
    rules: {
      validate: parties =>
        !(requirePartiesWhenPep && isPep && isCorporate && parties.length === 0) ||
        t('common:required')
    }
  });

  const fields = rawFields as (PepPartyFormValues & {id: string})[];

  useLayoutEffect(() => {
    if (isPep && !isCorporate && fields.length === 0) {
      append(EMPTY_PEP_PARTY, {shouldFocus: false});
    }
  }, [isPep, isCorporate, fields.length, append]);

  return {fields, append, remove, replace};
}
